/*
 * <sf-home-picker>: the homepage "Select your Vehicle" bar, cloned from the competitor hero.
 * Type (Car Covers / Seat Covers / Floor Mats) -> Year -> Make -> Model -> GO. Desktop (>=1024px) opens a dark
 * list with a search box over the field; phones use the native <select> (CSS shows one control per viewport,
 * this keeps both in sync). Lists, their order, the Type switch and the GO link come from
 * seatfit-vehicle-picker-lists.js and -kind.js, shared with the drawer, so both pickers offer the same vehicles
 * and open the same product URLs. The desktop list itself (rows, search, arrow keys) is
 * seatfit-home-picker-list.js, loaded `defer` before this file.
 */
(function () {
  'use strict';
  if (customElements.get('sf-home-picker')) return;
  var L = window.SeatFitPickerLists;
  var K = window.SeatFitPickerKind;
  var List = window.SeatFitHomePickerList;
  if (!L || !K || !List || !window.SeatFitPickerSearch) {
    console.warn('[home picker] helper scripts did not load; the vehicle bar is disabled');
    return;
  }
  var KEYS = ['type', 'year', 'make', 'model'];

  class SfHomePicker extends HTMLElement {
    connectedCallback() {
      if (this.fields) return; // the theme editor may re-attach the same node
      var self = this;
      this.pick = { year: null, make: null, model: null };
      this.kind = K.initial(this); // product in the Type field; this.index is its vehicle list
      this.index = null;
      this.loading = null;
      this.openField = null;
      this.emptyLabel = this.getAttribute('data-empty') || '';
      this.errorEl = this.querySelector('.sfh-pick__error');
      // A finished load, good or failed, redraws the fields; a failure also shows the error line.
      this.hooks = {
        busy: function (on) { if (!on) self.render(); },
        failed: function (on) { if (self.errorEl) self.errorEl.hidden = !on; }
      };
      this.goBtn = this.querySelector('.sfh-go');
      this.fields = {};
      this.querySelectorAll('.sfh-field').forEach(function (el) { self.wire(el); });
      if (this.goBtn) this.goBtn.addEventListener('click', function () { self.go(); });
      // The 84 KB vehicle list loads on the first sign of interest, or when the browser is idle.
      var warm = function () { self.load(); };
      ['pointerenter', 'focusin', 'touchstart'].forEach(function (t) { self.addEventListener(t, warm, { once: true, passive: true }); });
      if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 }); else setTimeout(warm, 2500);
      this.onDocDown = function (e) { if (self.openField && !self.openField.el.contains(e.target)) self.close(); };
      this.onPageShow = function (e) { if (e.persisted) { self.classList.remove('is-going'); self.render(); } };
      document.addEventListener('pointerdown', this.onDocDown, true);
      window.addEventListener('pageshow', this.onPageShow);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocDown, true);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    wire(el) {
      var self = this;
      var key = el.getAttribute('data-key');
      var f = {
        key: key, el: el, btn: el.querySelector('.sfh-field__btn'), val: el.querySelector('.sfh-field__val'),
        panel: el.querySelector('.sfh-field__panel'), search: el.querySelector('.sfh-field__search input'),
        list: el.querySelector('.sfh-field__list'), native: el.querySelector('.sfh-field__native'),
        options: [], value: null, sig: null, nsig: null
      };
      var first = f.native && f.native.options[0];
      f.ph = first ? first.cloneNode(true) : null;
      // Type lists the products Liquid printed (snippets/seatfit-type-options); they never change on the page.
      if (key === 'type' && first) {
        f.fixed = Array.prototype.map.call(f.native.options, function (o) { return { value: o.value, label: o.text }; });
      }
      f.btn.addEventListener('click', function () { if (self.openField === f) self.close(); else self.open(f); });
      f.panel.querySelector('.sfh-field__head').addEventListener('click', function () { self.close(); f.btn.focus(); });
      f.search.addEventListener('input', function () { List.filter(f); });
      f.search.addEventListener('keydown', function (e) {
        List.onKey(f, e, function (v) { self.choose(f, v); }, function (refocus) { self.close(); if (refocus) f.btn.focus(); });
      });
      f.list.addEventListener('mousedown', function (e) { e.preventDefault(); }); // focus stays in the search box
      f.list.addEventListener('click', function (e) {
        var li = e.target.closest ? e.target.closest('[data-v]') : null;
        if (li) self.choose(f, li.getAttribute('data-v'));
      });
      f.native.addEventListener('change', function () { self.choose(f, f.native.value || null, true); });
      this.fields[key] = f;
    }

    // A failure is retried on the next open; a success stays in this.index (per product kind).
    load() { return K.load(this, 15000, this.hooks); }

    // Type picked: the fields wait for that product's list, then keep the year/make/model it still offers.
    setKind(kind, fromNative) {
      var self = this;
      var moved = K.set(this, kind);
      this.close();
      this.render();
      this.load().then(function (idx) {
        if (idx && moved) self.pick = L.settle(idx, self.pick, null).pick;
        self.render();
        if (!fromNative) self.focusNext('type');
      });
    }

    states() {
      var st = L.fieldStates(this.index, this.pick, this.fields.type ? this.fields.type.fixed : null, this.kind);
      // Before the list arrives Year already shows as the next step (red outline), as on the competitor once
      // a type is chosen; opening it waits for the list.
      if (!this.index && st.year.state === 'off') { st.year.state = 'next'; st.year.ok = true; }
      return st;
    }

    render() {
      if (!this.fields) return;
      var st = this.states();
      var self = this;
      KEYS.forEach(function (k) { if (self.fields[k]) self.renderField(self.fields[k], st[k]); });
      // While another product's list loads, the kept vehicle is not checked against it yet: no GO.
      var complete = !!(this.index && this.pick.year && this.pick.make && this.pick.model);
      this.toggleAttribute('data-complete', complete);
      if (this.goBtn) this.goBtn.disabled = !complete || this.classList.contains('is-going');
    }

    renderField(f, st) {
      f.el.setAttribute('data-state', st.state);
      f.options = st.options;
      f.value = st.value == null ? null : String(st.value);
      f.btn.disabled = !st.ok;
      var label = '';
      f.options.forEach(function (o) { if (o.value === f.value) label = o.label; });
      f.val.textContent = label;
      f.el.toggleAttribute('data-filled', !!label);
      List.syncSelect(f, st.ok, !!this.index, this.emptyLabel);
      if (this.openField === f) List.fill(f, !!this.index, this.emptyLabel);
    }

    open(f) {
      if (f.btn.disabled) return;
      this.close();
      this.openField = f;
      f.panel.hidden = false;
      f.btn.setAttribute('aria-expanded', 'true');
      f.search.value = '';
      List.fill(f, !!this.index, this.emptyLabel);
      this.load();
      f.search.focus({ preventScroll: true });
    }

    close() {
      var f = this.openField;
      if (!f) return;
      this.openField = null;
      f.panel.hidden = true;
      f.btn.setAttribute('aria-expanded', 'false');
    }

    choose(f, value, fromNative) {
      if (f.key === 'type') return this.setKind(value, fromNative);
      if (value !== '__none') {
        this.pick[f.key] = value || null;
        if (this.index) this.pick = L.settle(this.index, this.pick, null).pick;
      }
      this.close();
      this.render();
      if (!fromNative) this.focusNext(f.key);
    }

    // Desktop keyboard/mouse flow: focus the field that now needs a value, or GO once all are set.
    focusNext(afterKey) {
      for (var i = KEYS.indexOf(afterKey) + 1; i < KEYS.length; i++) {
        var f = this.fields[KEYS[i]];
        if (f && f.el.getAttribute('data-state') === 'next' && !f.btn.disabled) { f.btn.focus(); return; }
      }
      if (this.goBtn && !this.goBtn.disabled) this.goBtn.focus();
    }

    go() {
      var p = this.pick;
      if (!(this.index && p.year && p.make && p.model)) return;
      var href = L.goUrl(K.productUrl(this.kind), p, location.href);
      this.classList.add('is-going');
      if (this.goBtn) this.goBtn.disabled = true;
      window.location.assign(href);
    }
  }

  customElements.define('sf-home-picker', SfHomePicker);
})();
